import { getRecordValue } from 'src/modules/shared/utils/record.util';
import React from 'react';
import type { TCAction } from 'src/api/generated';
import { extractInputRefs } from 'src/modules/automations/utils/workflows/refs.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useActions } from 'src/modules/shared/hooks/api/automations/actions';
import WorkflowInputEditor from './workflow-input-editor';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { WORKFLOW_START_NODE_ID } from '../workflow-canvas/workflow-canvas';
import { type ReferenceOption, type StepOutputOption } from './types';
import { getSchemaProps, isPlainObject } from './workflow-schema-utils';
import StepInputsTab from './step-inputs-tab';
import type {
  WorkflowDefinitionDraft,
  WorkflowVersionDefinitionStep,
} from 'src/modules/tools/utils/workflow-definition.type';

const buildReverseEdges = (
  steps: Record<string, WorkflowVersionDefinitionStep>
): Partial<Record<string, string[]>> => {
  const reverse: Partial<Record<string, string[]>> = {};
  for (const step of Object.values(steps)) {
    for (const nextId of step.nextStepIds) {
      reverse[nextId] ??= [];
      reverse[nextId].push(step.id);
    }
  }
  return reverse;
};

const getAncestorStepIds = (
  steps: Record<string, WorkflowVersionDefinitionStep>,
  targetId: string
): string[] => {
  const reverse = buildReverseEdges(steps);
  const visited = new Set<string>();
  const stack = [...(reverse[targetId] ?? [])];
  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;
    if (visited.has(current)) continue;
    visited.add(current);
    (reverse[current] ?? []).forEach((id) => {
      if (!visited.has(id)) stack.push(id);
    });
  }
  return Array.from(visited);
};

const findActionById = (
  actions: TCAction[],
  actionId?: string
): TCAction | undefined => {
  if (!actionId) return undefined;
  const normalized = actionId.trim();
  return actions.find(
    (a) =>
      a.name === normalized ||
      a.annotations?.configurationAlias === normalized ||
      a.annotations?.baseToolName === normalized
  );
};

/** Workflow Details Panel Props contract. */
interface WorkflowDetailsPanelProps {
  /** Workflow definition displayed by the editor. */
  definition?: WorkflowDefinitionDraft;
  /** Identifier of the currently selected workflow step. */
  selectedStepId?: string;
  /** Whether workflow editing controls are available. */
  editable?: boolean;
  /** Schema describing the workflow inputs. */
  inputSchema?: Record<string, unknown>;
  /** Updates the workflow input schema. */
  onChangeInputSchema?: (schema: Record<string, unknown>) => void;
  /** Updates the input mappings for a step. */
  onChangeStepInputs?: (
    stepId: string,
    inputs: Record<string, unknown>
  ) => void;
  /** Reports whether input mappings are valid. */
  onValidationChange?: (errors: string[]) => void;
  /** Editable workflow title and description. */
  metadata?: { title?: string; description?: string };
  /** Updates the workflow metadata. */
  onChangeMetadata?: (meta: { title?: string; description?: string }) => void;
  /** Removes the selected step from the workflow. */
  onDeleteStep?: (stepId: string) => void;
}

const PanelSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="space-y-2">
    <Text as="h4" size="small" weight="medium" className="text-slate-600">
      {title}
    </Text>
    {children}
  </div>
);

export const WorkflowDetailsPanel: React.FC<WorkflowDetailsPanelProps> = ({
  definition,
  selectedStepId,
  editable = false,
  inputSchema,
  onChangeInputSchema,
  onChangeStepInputs,
  onValidationChange,
  metadata,
  onChangeMetadata,
  onDeleteStep,
}) => {
  const [activeTab, setActiveTab] = React.useState<'details' | 'inputs'>(
    'details'
  );

  const isWorkflowInputsSelected = selectedStepId === WORKFLOW_START_NODE_ID;

  const step: WorkflowVersionDefinitionStep | undefined = React.useMemo(() => {
    if (!definition || !selectedStepId) return undefined;
    if (selectedStepId === WORKFLOW_START_NODE_ID) return undefined;
    if (definition.finalStep && definition.finalStep.id === selectedStepId) {
      return {
        ...definition.finalStep,
        nextStepIds: [],
      } as WorkflowVersionDefinitionStep;
    }
    const steps = definition.steps;
    if (!steps) return undefined;
    return getRecordValue(steps, selectedStepId);
  }, [definition, selectedStepId]);

  const stepInputs = React.useMemo(() => step?.inputs ?? {}, [step]);
  const inputRefs = React.useMemo(
    () => extractInputRefs(stepInputs),
    [stepInputs]
  );

  const effectiveInputSchema = React.useMemo(() => {
    if (inputSchema) return inputSchema;
    const schema = definition?.inputSchema;
    if (!isPlainObject(schema)) return undefined;
    return schema;
  }, [inputSchema, definition]);

  const workflowInputFields = React.useMemo(() => {
    if (!effectiveInputSchema) return [];
    const props = (effectiveInputSchema as { properties?: unknown }).properties;
    const requiredRaw = (effectiveInputSchema as { required?: unknown })
      .required;

    const properties = props && isPlainObject(props) ? props : undefined;
    const requiredSet = new Set(
      Array.isArray(requiredRaw)
        ? (requiredRaw as unknown[]).filter(
            (k): k is string => typeof k === 'string'
          )
        : []
    );

    if (!properties) return [];

    return Object.keys(properties).map((key) => ({
      key,
      required: requiredSet.has(key),
    }));
  }, [effectiveInputSchema]);

  const shouldFetchActions =
    editable &&
    Boolean(selectedStepId && selectedStepId !== WORKFLOW_START_NODE_ID);
  const {
    actions,
    loading: actionsLoading,
    error: actionsError,
  } = useActions(undefined, shouldFetchActions, true);

  const selectedAction = React.useMemo(() => {
    if (!step) return undefined;
    return findActionById(actions, step.actionId);
  }, [actions, step]);

  const stepOutputOptions = React.useMemo((): StepOutputOption[] => {
    if (!definition?.steps) return [];
    if (!selectedStepId) return [];

    const steps = definition.steps;
    const isFinalSelected = definition.finalStep?.id === selectedStepId;
    const candidateStepIds = isFinalSelected
      ? Object.keys(steps)
      : getRecordValue(steps, selectedStepId)
        ? getAncestorStepIds(steps, selectedStepId)
        : [];

    return candidateStepIds
      .map((id) => {
        const s = getRecordValue(steps, id);
        if (!s) return null;
        const act = findActionById(actions, s.actionId);
        const outProps = act ? getSchemaProps(act.outputSchema) : {};
        const outputKeys = Object.keys(outProps).sort();
        return {
          stepId: id,
          actionId: s.actionId,
          outputKeys,
          outputSchema: isPlainObject(act?.outputSchema)
            ? act.outputSchema
            : undefined,
        } satisfies StepOutputOption;
      })
      .filter((v): v is StepOutputOption => Boolean(v));
  }, [actions, definition?.finalStep?.id, definition?.steps, selectedStepId]);

  const referenceOptions = React.useMemo((): ReferenceOption[] => {
    const options: ReferenceOption[] = [];
    workflowInputFields.forEach(({ key }) => {
      options.push({
        id: `inputs:${key}`,
        label: `/inputs.${key}`,
        template: `{{inputs.${key}}}`,
      });
    });
    stepOutputOptions.forEach((stepOpt) => {
      stepOpt.outputKeys.forEach((k) => {
        options.push({
          id: `steps:${stepOpt.stepId}:${k}`,
          label: `/steps.${stepOpt.stepId}.outputs.${k}`,
          template: `{{steps.${stepOpt.stepId}.outputs.${k}}}`,
        });
      });
    });
    return options;
  }, [stepOutputOptions, workflowInputFields]);

  const handleChangeInputs = React.useCallback(
    (nextInputs: Record<string, unknown>) => {
      if (!selectedStepId) return;
      if (!onChangeStepInputs) return;
      onChangeStepInputs(selectedStepId, nextInputs);
    },
    [onChangeStepInputs, selectedStepId]
  );

  const renderPanelContent = () => {
    if (isWorkflowInputsSelected) {
      if (editable) {
        return (
          <WorkflowInputEditor
            value={effectiveInputSchema}
            onChange={onChangeInputSchema}
            onValidationChange={onValidationChange}
          />
        );
      }
      if (workflowInputFields.length > 0) {
        return (
          <div className="space-y-4 text-sm text-slate-600">
            <PanelSection title="Entradas del flujo de trabajo">
              <ul className="list-disc list-inside space-y-1">
                {workflowInputFields.map((field) => (
                  <li key={field.key}>
                    <span className="font-medium text-slate-800">
                      {field.key}
                    </span>
                    {field.required && (
                      <span className="ml-1 text-xs text-rose-600">
                        (requerido)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </PanelSection>
          </div>
        );
      }
      return (
        <EmptyState
          description="Este flujo no tiene entradas definidas."
          appearance="soft"
          variant="neutral"
          size="sm"
          className="py-6"
        />
      );
    }
    if (!step) {
      return (
        <EmptyState
          description="Selecciona un paso para ver la información."
          appearance="soft"
          variant="neutral"
          size="sm"
          className="py-6"
        />
      );
    }
    if (activeTab === 'details') {
      return (
        <div className="space-y-4 text-sm text-slate-600">
          <PanelSection title="Acción">
            <div className="text-slate-800 font-medium">{step.actionId}</div>
            {step.description && <p>{step.description}</p>}
          </PanelSection>
          <PanelSection title="Siguientes pasos">
            {step.nextStepIds.length > 0 ? (
              <ul className="list-disc list-inside space-y-1">
                {step.nextStepIds.map((nextId: string) => (
                  <li key={nextId}>{nextId}</li>
                ))}
              </ul>
            ) : (
              <Text size="tiny" className="text-slate-500">
                No hay pasos siguientes.
              </Text>
            )}
          </PanelSection>
        </div>
      );
    }
    return (
      <StepInputsTab
        editable={Boolean(editable && onChangeStepInputs)}
        stepId={step.id}
        stepInputs={stepInputs}
        selectedAction={selectedAction}
        actionsLoading={actionsLoading}
        actionsError={actionsError}
        workflowInputFields={workflowInputFields}
        effectiveInputSchema={
          isPlainObject(effectiveInputSchema) ? effectiveInputSchema : undefined
        }
        stepOutputOptions={stepOutputOptions}
        referenceOptions={referenceOptions}
        inputRefs={inputRefs}
        onChangeInputs={handleChangeInputs}
      />
    );
  };

  if (!definition) {
    return (
      <EmptyState
        description="No hay definición disponible."
        appearance="soft"
        variant="neutral"
        size="sm"
      />
    );
  }

  if (!selectedStepId) {
    return (
      <aside className="w-full lg:w-[360px] border-t lg:border-t-0 lg:border-l bg-white flex flex-col p-4 space-y-4">
        <PanelSection title="Metadatos del flujo">
          <div className="space-y-2">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="workflow-title"
                className="text-xs text-slate-600"
              >
                Título
              </label>
              <input
                id="workflow-title"
                className="border rounded px-2 py-1.5 text-sm w-full"
                value={metadata?.title ?? ''}
                onChange={(e) =>
                  onChangeMetadata?.({
                    ...(metadata ?? {}),
                    title: e.target.value,
                  })
                }
                placeholder="Nombre del flujo"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="workflow-description"
                className="text-xs text-slate-600"
              >
                Descripción
              </label>
              <textarea
                id="workflow-description"
                className="border rounded px-2 py-1.5 text-sm w-full min-h-[96px]"
                value={metadata?.description ?? ''}
                onChange={(e) =>
                  onChangeMetadata?.({
                    ...(metadata ?? {}),
                    description: e.target.value,
                  })
                }
                placeholder="Describe brevemente el propósito del flujo"
              />
            </div>
          </div>
        </PanelSection>
      </aside>
    );
  }

  return (
    <aside className="w-full lg:w-[360px] border-t lg:border-t-0 lg:border-l bg-white flex flex-col">
      <div className="px-4 py-3 border-b">
        <Text as="h3" size="h4" weight="medium">
          {isWorkflowInputsSelected
            ? 'Entradas del flujo de trabajo'
            : step
              ? step.id
              : 'Selecciona un paso'}
        </Text>
        <Text size="small" className="text-slate-500">
          {isWorkflowInputsSelected
            ? 'Campos de entrada definidos para este flujo.'
            : step
              ? step.actionId
              : 'Haz clic en un nodo para ver los detalles.'}
        </Text>
      </div>

      {!isWorkflowInputsSelected && (
        <div className="px-4 pt-3">
          <Tabs
            value={activeTab}
            onChange={(value) => setActiveTab(value as typeof activeTab)}
            appearance="soft"
            size="sm"
          >
            <Tab value="details" label="Detalles" />
            <Tab value="inputs" label="Entradas" />
          </Tabs>
        </div>
      )}

      <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
        {renderPanelContent()}
        {step && editable && onDeleteStep ? (
          <div className="pt-2">
            <Button
              variant="danger"
              appearance="ghost"
              size="sm"
              onClick={() => onDeleteStep(step.id)}
            >
              Eliminar paso
            </Button>
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export default WorkflowDetailsPanel;
